import { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import {
  ManageStudiesActionType,
  useManageStudiesContext,
} from "../../../context/ManageStudiesContext";
import useAlert from "../../../hooks/useAlert";
import { IFormattedMessage } from "../../../models/message.interface";
import {
  AddStudyFailureReason,
  IAddStudyResult,
} from "../../../models/results/add-study-result.interface";
import { AddStudy } from "../../../models/study.interface";
import studiesService from "../../../services/studies.service";
import { isNumber } from "../../../utils/sharedUtil";
import AlertModal from "../../Shared/AlertModal/AlertModal";
import DeleteWarningModal from "../../Shared/DeleteWarningModal/DeleteWarningModal";
import { Loader } from "@shltelemedicine/shl-ui-lib";
import AddStudyBanner from "./AddStudyBanner/AddStudyBanner";
import AddStudyModal from "./AddStudyModal/AddStudyModal";
import LinkUsersToStudyModal from "./LinkUsersToStudyModal/LinkUsersToStudyModal";
import ManageStudiesHeader from "./ManageStudiesHeader/ManageStudiesHeader";
import ManageStudiesTree from "./ManageStudiesTree/ManageStudiesTree";

const ManageStudies = () => {
  const {
    state: {
      loading,
      orgStudies: {
        studies,
        totalStudies,
        totalActiveStudies,
        totalUsers,
        totalActiveUsers,
        totalSubjects,
      },
      selectedStudy,
      showLinkUsersToStudyModal,
      showDeleteStudyModal,
      studyModal: { show: showStudyModal, mode: studyModalMode },
    },
    loadStudies,
    dispatch,
  } = useManageStudiesContext();

  const {
    showErrorAlert,
    alertProps,
    alertMessage,
    getAlertMessageAndSupportContent,
  } = useAlert();

  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    (async () => {
      await loadStudies();
    })();
  }, []); // eslint-disable-line

  const addStudyClickHandler = () =>
    dispatch({
      type: ManageStudiesActionType.SET_STUDY_MODAL,
      payload: {
        mode: "add",
        show: true,
      },
    });

  const saveStudyHandler = async (study: AddStudy | Partial<AddStudy>) => {
    const isEdit = studyModalMode === "edit";

    const request = !isEdit
      ? studiesService.addStudy(study as AddStudy)
      : studiesService.updateStudy({
          id: selectedStudy?.id!,
          ...study,
        });

    const response = await request;
    const { success, errorCode } = response;
    const failReason = !isEdit
      ? (response as IAddStudyResult).failReason
      : AddStudyFailureReason.Unknown;

    if (!success) {
      const { id, defaultMessage, values } = getAddStudyFailureMsg(
        failReason,
        isEdit
      );
      const { message, support } = getAlertMessageAndSupportContent(
        id,
        defaultMessage,
        values,
        errorCode
      );
      showErrorAlert(message, support);
      return;
    }
    closeStudyModal();
    await loadStudies();
  };

  const deleteStudyHandler = async () => {
    setDeleting(true);

    try {
      const { success, errorCode } = await studiesService.deleteStudy(
        selectedStudy!.id
      );

      if (!success) {
        const { message, support } = getAlertMessageAndSupportContent(
          "ERROR.DELETE_STUDY",
          "An error occurred while trying to delete a study.<br></br>Please try again.",
          {
            br: () => <br></br>,
          },
          errorCode
        );
        showErrorAlert(message, support);
        return;
      }

      dispatch({
        type: ManageStudiesActionType.SET_DELETE_STUDY_MODAL,
        payload: false,
      });

      await loadStudies();
    } finally {
      setDeleting(false);
    }
  };

  const linkUsersToStudyHandler = () => {};

  const closeStudyModal = () => {
    dispatch({
      type: ManageStudiesActionType.SET_STUDY_MODAL,
      payload: {
        show: false,
      },
    });
  };

  const closeDeleteStudyModal = () => {
    dispatch({
      type: ManageStudiesActionType.SET_DELETE_STUDY_MODAL,
      payload: false,
    });
  };

  const closeLinkUsersToStudyModal = () => {
    dispatch({
      type: ManageStudiesActionType.SET_LINK_USERS_MODAL,
      payload: false,
    });
  };

  const inactiveStudies =
    isNumber(totalStudies) && isNumber(totalActiveStudies)
      ? totalStudies! - totalActiveStudies!
      : undefined;

  const inactiveUsers =
    isNumber(totalUsers) && isNumber(totalActiveUsers)
      ? totalUsers! - totalActiveUsers!
      : undefined;

  const getAddStudyFailureMsg = (
    failureReason: AddStudyFailureReason | undefined,
    isEdit: boolean
  ): IFormattedMessage => {
    if (!failureReason) return {} as IFormattedMessage;

    switch (failureReason) {
      case AddStudyFailureReason.StudyNameAlreadyInUse:
        return {
          id: "ERROR.ADD_STUDY.NAME_ALREADY_EXISTS",
          defaultMessage:
            "This study name cannot be used, it already exists in our system.<br></br>Please try again.",
          values: {
            br: () => <br></br>,
          },
        };
      default:
        return {
          id: !isEdit ? "ERROR.ADD_STUDY" : "ERROR.EDIT_STUDY",
          defaultMessage: `An error occurred while ${
            !isEdit ? "adding" : "updating"
          } a study.<br></br>Please try again`,
          values: {
            br: () => <br></br>,
          },
        };
    }
  };

  return (
    <>
      <ManageStudiesHeader
        studies={{
          total: totalStudies,
          active: totalActiveStudies,
          inactive: inactiveStudies,
        }}
        users={{
          total: totalUsers,
          active: totalActiveUsers,
          inactive: inactiveUsers,
        }}
        totalSubjects={totalSubjects}
      />
      <div className="p-4">
        {loading ? (
          <Loader />
        ) : (
          <>
            <ManageStudiesTree studies={studies} />

            <AddStudyBanner
              hasStudies={!!studies.length}
              onClick={addStudyClickHandler}
            />
          </>
        )}
      </div>

      <AddStudyModal
        show={showStudyModal}
        mode={studyModalMode ?? "add"}
        study={selectedStudy ?? undefined}
        onSave={saveStudyHandler}
        onClose={closeStudyModal}
        editStudyName={!!selectedStudy?.totalEcgs}
      />

      <LinkUsersToStudyModal
        show={showLinkUsersToStudyModal}
        study={
          selectedStudy
            ? {
                id: selectedStudy.id,
                name: selectedStudy.name,
                totalLinkedUsers: selectedStudy.totalActiveLinkedUsers,
                currentUsers: selectedStudy.users ?? [],
              }
            : undefined
        }
        onSave={linkUsersToStudyHandler}
        onCancel={closeLinkUsersToStudyModal}
      />

      <DeleteWarningModal
        show={showDeleteStudyModal}
        loading={deleting}
        onClose={closeDeleteStudyModal}
        onConfirm={deleteStudyHandler}
      >
        <FormattedMessage
          id="MANAGEMENT.STUDIES.REMOVE_STUDY"
          defaultMessage="You requested to delete a study."
          values={{
            br: () => <br></br>,
          }}
        />
      </DeleteWarningModal>

      <AlertModal {...alertProps}>{alertMessage}</AlertModal>
    </>
  );
};

export default ManageStudies;
